import React, { useCallback, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import ReportScopeToggle from "components/reports/shared/ReportScopeToggle";
import {
  CashFlowForecast,
  DiscountLeakage,
} from "./InvoiceAnalyticsReports";
import { getChartColors } from "theme";
import api from "services/api";

const formatCurrency = (value) =>
  `$${(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Outstanding Invoices Report - AR risk and collection health dashboard
 */
export const OutstandingInvoices = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [openOnly, setOpenOnly] = useState(true);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/invoice/outstanding", {
      params: { ...activeFilters, openOnly: openOnly ? 1 : 0 },
    });
    return response.data;
  }, [openOnly]);

  const openInvoiceDrillDown = useCallback((title, subtitle, description, rows) => {
    const sortedRows = [...rows].sort((a, b) => {
      const aDays = a.daysPastDue == null ? -9999 : a.daysPastDue;
      const bDays = b.daysPastDue == null ? -9999 : b.daysPastDue;
      if (bDays !== aDays) return bDays - aDays;
      return (b.outstandingAmount || 0) - (a.outstandingAmount || 0);
    });

    const totalOutstanding = sortedRows.reduce(
      (sum, r) => sum + (r.outstandingAmount || 0),
      0,
    );
    const overdueRows = sortedRows.filter(
      (r) => r.daysPastDue != null && r.daysPastDue > 0,
    );
    const avgDays = overdueRows.length
      ? overdueRows.reduce((sum, r) => sum + (r.daysPastDue || 0), 0) /
        overdueRows.length
      : 0;

    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: sortedRows,
      columns: [
        {
          field: "invoiceNumber",
          headerName: "Invoice #",
          width: 120,
        },
        {
          field: "clientName",
          headerName: "Client",
          width: 220,
        },
        {
          field: "invoiceDate",
          headerName: "Invoice Date",
          width: 130,
          renderCell: (row) => formatDate(row.invoiceDate),
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          width: 130,
          renderCell: (row) => formatDate(row.dueDate),
        },
        {
          field: "daysPastDue",
          headerName: "Days Past Due",
          width: 120,
          renderCell: (row) =>
            row.daysPastDue == null
              ? "-"
              : row.daysPastDue > 0
                ? row.daysPastDue
                : 0,
        },
        {
          field: "outstandingAmount",
          headerName: "Outstanding",
          width: 150,
          renderCell: (row) => formatCurrency(row.outstandingAmount),
        },
        {
          field: "billedAmount",
          headerName: "Billed",
          width: 140,
          renderCell: (row) => formatCurrency(row.billedAmount),
        },
        {
          field: "paidAmount",
          headerName: "Collected",
          width: 140,
          renderCell: (row) => formatCurrency(row.paidAmount),
        },
        {
          field: "collectionProgress",
          headerName: "Collected %",
          width: 120,
          renderCell: (row) => `${(row.collectionProgress || 0).toFixed(1)}%`,
        },
        {
          field: "servicesCount",
          headerName: "Services",
          width: 100,
        },
        {
          field: "unpaidServicesCount",
          headerName: "Unpaid Services",
          width: 130,
        },
        {
          field: "agingBucket",
          headerName: "Aging Bucket",
          width: 120,
        },
        {
          field: "riskLevel",
          headerName: "Risk",
          width: 100,
          renderCell: (row) => (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color:
                  row.riskLevel === "Critical"
                    ? "error.main"
                    : row.riskLevel === "High"
                      ? "warning.main"
                      : row.riskLevel === "Medium"
                        ? "info.main"
                        : "success.main",
              }}
            >
              {row.riskLevel}
            </Typography>
          ),
        },
      ],
      summary: {
        metrics: [
          {
            label: "Invoices",
            value: sortedRows.length,
          },
          {
            label: "Outstanding",
            value: formatCurrency(totalOutstanding),
          },
          {
            label: "Overdue Invoices",
            value: overdueRows.length,
          },
          {
            label: "Avg Days Past Due",
            value: `${avgDays.toFixed(1)} days`,
          },
        ],
      },
    });
  }, []);

  const handleOutstandingClick = useCallback(
    (data) => {
      const rows = data.invoices || [];
      openInvoiceDrillDown(
        "Outstanding Invoices - Full Ledger",
        `${rows.length} invoices • ${formatCurrency(data.summary?.totalOutstandingAmount || 0)} outstanding`,
        <Box component="div" sx={{ typography: "body2", lineHeight: 1.6, color: "text.secondary" }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "info.main" }}>
            Outstanding Invoices (Invoice-level)
          </Typography>
          <Typography variant="body2" paragraph>
            Outstanding amount follows the same invoice logic as the Invoices screen: unpaid when Payment Received Date is empty, using Invoice Amount.
          </Typography>
          <Typography variant="body2" component="div">
            • Unpaid Invoice: Payment Received Date is null
            <br />• Outstanding Amount: Invoice Amount for unpaid invoices
            <br />• Overdue: Unpaid invoice with due date before today
          </Typography>
        </Box>,
        rows,
      );
    },
    [openInvoiceDrillDown],
  );

  const handleOverdueClick = useCallback(
    (data) => {
      const rows = (data.invoices || []).filter(
        (r) => r.daysPastDue != null && r.daysPastDue > 0,
      );
      openInvoiceDrillDown(
        "Overdue Invoices",
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, r) => sum + (r.outstandingAmount || 0), 0),
        )} overdue`,
        "Shows only overdue invoices (past due date) to prioritize collection efforts.",
        rows,
      );
    },
    [openInvoiceDrillDown],
  );

  const handleCriticalClick = useCallback(
    (data) => {
      const rows = (data.invoices || []).filter(
        (r) => r.daysPastDue != null && r.daysPastDue > 90,
      );
      openInvoiceDrillDown(
        "Critical Aging (90+ Days)",
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, r) => sum + (r.outstandingAmount || 0), 0),
        )} at critical risk`,
        "High-risk receivables aged 90+ days. These should be prioritized for escalated collection workflows.",
        rows,
      );
    },
    [openInvoiceDrillDown],
  );

  const handleDueSoonClick = useCallback(
    (data) => {
      const rows = (data.invoices || []).filter(
        (r) => r.daysPastDue != null && r.daysPastDue <= 7 && r.daysPastDue >= -7,
      );
      openInvoiceDrillDown(
        "Due Soon / Recently Due",
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, r) => sum + (r.outstandingAmount || 0), 0),
        )}`,
        "Invoices due within 7 days (or overdue by up to 7 days), useful for proactive follow-ups before balances age further.",
        rows,
      );
    },
    [openInvoiceDrillDown],
  );

  const handlePrivatePersonsUnpaidClick = useCallback(
    (data) => {
      const privateData = data.privatePersonsUnpaid || {};
      const rows = privateData.services || [];
      const sortedRows = [...rows].sort((a, b) => (b.amount || 0) - (a.amount || 0));

      setDrillDown({
        open: true,
        title: "Private Persons - Unpaid Services",
        subtitle: `${sortedRows.length} unpaid services • ${formatCurrency(
          privateData.totalAmount || 0,
        )}`,
        description:
          "Tracks completed private-person services that are not paid yet (and are intentionally not invoiced).",
        data: sortedRows,
        columns: [
          { field: "serviceId", headerName: "Service #", width: 110 },
          {
            field: "serviceDate",
            headerName: "Service Date",
            width: 130,
            renderCell: (row) => formatDate(row.serviceDate),
          },
          { field: "serviceName", headerName: "Service", width: 240 },
          { field: "requestorName", headerName: "Requestor", width: 180 },
          { field: "serviceCategory", headerName: "Category", width: 130 },
          {
            field: "amount",
            headerName: "Amount",
            width: 140,
            renderCell: (row) => formatCurrency(row.amount),
          },
          {
            field: "paid",
            headerName: "Paid",
            width: 90,
            renderCell: (row) => (row.paid ? "Yes" : "No"),
          },
          {
            field: "invoiced",
            headerName: "Invoiced",
            width: 100,
            renderCell: (row) => (row.invoiced ? "Yes" : "No"),
          },
        ],
        summary: {
          metrics: [
            { label: "Unpaid Services", value: sortedRows.length },
            { label: "Total Amount", value: formatCurrency(privateData.totalAmount || 0) },
          ],
        },
      });
    },
    [setDrillDown],
  );

  const handleAgingSliceClick = useCallback(
    (slice, data) => {
      const rows = (data.invoices || []).filter((r) => r.agingBucket === slice.label);
      openInvoiceDrillDown(
        `Aging Bucket - ${slice.label}`,
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, r) => sum + (r.outstandingAmount || 0), 0),
        )}`,
        `Invoices categorized in ${slice.label} based on due date aging.`,
        rows,
      );
    },
    [openInvoiceDrillDown],
  );

  const handleClientBarClick = useCallback(
    (bar, data) => {
      const rows = (data.invoices || []).filter((r) => r.clientName === bar.clientName);
      openInvoiceDrillDown(
        `Client Outstanding - ${bar.clientName}`,
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, r) => sum + (r.outstandingAmount || 0), 0),
        )}`,
        "All outstanding invoices for the selected client.",
        rows,
      );
    },
    [openInvoiceDrillDown],
  );

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      return [
        {
          groupTitle: "AR Exposure",
          metrics: [
            {
              title: "Total Outstanding",
              value: formatCurrency(summary.totalOutstandingAmount || 0),
              subtitle: `${summary.totalOutstandingInvoices || 0} open invoices`,
              color: "warning.main",
              tooltipTitle: "Total Outstanding",
              tooltipContent:
                "Definition: Sum of invoice amounts for all invoices that are still unpaid in the selected filter range.\n\nThis is your total open AR exposure.\n\nClick to open the full outstanding ledger and review invoice-level details.",
              onClick: () => handleOutstandingClick(data),
            },
            {
              title: "Overdue Amount",
              value: formatCurrency(summary.overdueAmount || 0),
              subtitle: `${summary.overdueInvoices || 0} overdue invoices`,
              color: "error.main",
              tooltipTitle: "Overdue Amount",
              tooltipContent:
                "Definition: Outstanding amount for unpaid invoices whose due date is before today.\n\nThis is late AR requiring immediate follow-up.\n\nClick to open only overdue invoices and prioritize collections.",
              onClick: () => handleOverdueClick(data),
            },
            {
              title: "Critical 90+ Days",
              value: formatCurrency(summary.criticalAmount || 0),
              subtitle: `${summary.criticalInvoices || 0} invoices at high risk`,
              color: "error.main",
              tooltipTitle: "Critical 90+ Days",
              tooltipContent:
                "Definition: Unpaid invoices aged more than 90 days past due.\n\nThese balances carry the highest collection risk and potential write-off concern.\n\nClick to open this critical queue for escalated action.",
              onClick: () => handleCriticalClick(data),
            },
            {
              title: "Due Soon (±7 Days)",
              value: formatCurrency(summary.dueSoonAmount || 0),
              subtitle: `${summary.dueSoonInvoices || 0} invoices`,
              color: "info.main",
              tooltipTitle: "Due Soon (±7 Days)",
              tooltipContent:
                "Definition: Invoices due within 7 days plus invoices recently overdue by up to 7 days.\n\nThis supports proactive outreach before balances age into higher-risk buckets.\n\nClick to open the near-due and just-overdue list.",
              onClick: () => handleDueSoonClick(data),
            },
          ],
        },
        {
          groupTitle: "Collection Health",
          metrics: [
            {
              title: "Avg Days Past Due",
              value: `${(summary.avgDaysPastDue || 0).toFixed(1)} days`,
              subtitle: "Overdue invoices only",
              tooltipTitle: "Average Days Past Due",
              tooltipContent:
                "Definition: Average number of days overdue across currently overdue invoices only.\n\nHigher values indicate slower collections and deeper aging pressure.\n\nClick to open overdue invoices contributing to this average.",
              onClick: () => handleOverdueClick(data),
            },
            {
              title: "Collection Rate",
              value: `${(summary.collectionRate || 0).toFixed(2)}%`,
              subtitle: "Paid invoices / total invoiced amount",
              tooltipTitle: "Collection Rate",
              tooltipContent:
                "Definition: Percentage of invoiced dollars that have been collected.\n\nFormula: Collected Amount ÷ Total Invoiced Amount × 100.\n\nUse this KPI to monitor overall AR conversion performance over time.",
            },
            {
              title: "Private Persons Unpaid",
              value: `${data.privatePersonsUnpaid?.servicesCount || 0}`,
              subtitle: `${formatCurrency(data.privatePersonsUnpaid?.totalAmount || 0)} not yet collected`,
              color: "warning.main",
              tooltipTitle: "Private Persons Unpaid",
              tooltipContent:
                "Definition: Completed private-person services that are not yet marked paid (typically not invoiced by design).\n\nThis queue highlights direct collection follow-up outside normal invoice flow.\n\nClick to open all unpaid private-person services with service-level details.",
              onClick: () => handlePrivatePersonsUnpaidClick(data),
            },
          ],
        },
      ];
    },
    [
      handleOutstandingClick,
      handleOverdueClick,
      handleCriticalClick,
      handleDueSoonClick,
      handlePrivatePersonsUnpaidClick,
    ],
  );

  const chartsConfig = useCallback(
    (data) => {
      const monthly = data.monthlyTrend || {};
      const monthRows = (monthly.months || []).map((month, idx) => ({
        month,
        outstandingAmount: monthly.outstandingAmount?.[idx] || 0,
        overdueAmount: monthly.overdueAmount?.[idx] || 0,
        invoiceCount: monthly.invoiceCount?.[idx] || 0,
      }));

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Outstanding by Invoice Month"
              data={monthRows
                .filter((m) => (m.outstandingAmount || 0) > 0)
                .map((m, idx) => ({
                  id: idx + 1,
                  label: m.month,
                  value: m.outstandingAmount || 0,
                }))}
              height={350}
              valueFormatter={(value) => formatCurrency(value)}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              tooltipTitle="Monthly AR Trend"
              tooltipContent={
                "Distribution of currently outstanding dollars by invoice month.\n\nHow to read:\n- Older months with large shares indicate aging backlog.\n- Newer-month concentration can signal recent billing growth outpacing collections.\n\nUse alongside aging buckets to separate recency vs severity risk."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Outstanding by Aging Bucket"
              data={(data.agingData || []).map((a) => ({
                id: a.id,
                label: `${a.label} (${a.invoiceCount})`,
                value: a.value,
              }))}
              height={350}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              valueFormatter={(value) => formatCurrency(value)}
              tooltipTitle="Aging Distribution"
              tooltipContent={
                "Breaks outstanding balances into aging buckets (Current, 1-30, 31-60, 61-90, 90+).\n\nHow to read:\n- Movement toward older buckets indicates worsening AR health.\n- Larger 61+ day concentration suggests higher recovery risk.\n\nClick any slice to drill into the invoices behind that bucket."
              }
              onSliceClick={(item) => handleAgingSliceClick(item, data)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Top Clients by Outstanding Balance"
              data={data.topClients || []}
              xAxisKey="clientName"
              series={[
                {
                  dataKey: "outstandingAmount",
                  label: "Outstanding ($)",
                  color: chartColors.warning,
                },
                {
                  dataKey: "overdueAmount",
                  label: "Overdue ($)",
                  color: chartColors.error,
                },
              ]}
              height={350}
              yAxisLabel="Amount ($)"
              yAxisScaleType="linear"
              valueFormatter={(value) => formatCurrency(value)}
              onBarClick={(item) => handleClientBarClick(item, data)}
              tooltipTitle="Client Exposure"
              tooltipContent={
                "Compares outstanding vs overdue dollars by top clients.\n\nHow to read:\n- High outstanding + high overdue on one client indicates concentration risk.\n- Outstanding with low overdue may indicate healthy, recent receivables.\n\nClick a bar to open invoice-level exposure for that client."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Private Persons Unpaid by Category"
              data={data.privatePersonsUnpaid?.byCategory || []}
              xAxisKey="category"
              series={[
                {
                  dataKey: "amount",
                  label: "Unpaid Amount ($)",
                  color: chartColors.warning,
                },
                {
                  dataKey: "servicesCount",
                  label: "Services",
                  color: chartColors.info,
                  valueFormatter: (value) => `${(value || 0).toLocaleString()} services`,
                },
              ]}
              height={350}
              yAxisLabel="Amount / Count"
              yAxisScaleType="linear"
              valueFormatter={(value) => formatCurrency(value)}
              tooltipKey="category"
              onBarClick={() => handlePrivatePersonsUnpaidClick(data)}
              tooltipTitle="Private Persons Collection Queue"
              tooltipContent={
                "Shows unpaid private-person services grouped by service category.\n\nIncludes both value and service count to prioritize effort by financial impact and workload.\n\nClick to open service-level collection queue for follow-up and payment updates."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Collection Stage by Services"
              data={(data.collectionStageData || []).map((stage, idx) => ({
                id: idx + 1,
                label: `${stage.stage} (${stage.servicesCount || 0})`,
                value: stage.servicesCount || 0,
              }))}
              height={350}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              valueFormatter={(value) => `${(value || 0).toLocaleString()} services`}
              tooltipTitle="Collection Stage"
              tooltipContent={
                "Distribution of linked services across collection stages (paid vs unpaid states), measured by service count.\n\nUseful for operational workload planning.\n\nCompare with the amount-based chart to distinguish volume-heavy vs value-heavy stages."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Collection Stage by Amount"
              data={(data.collectionStageData || [])
                .filter((stage) => (stage.totalAmount || 0) > 0)
                .map((stage, idx) => ({
                  id: idx + 1,
                  label: `${stage.stage} (${formatCurrency(stage.totalAmount || 0)})`,
                  value: stage.totalAmount || 0,
                }))}
              height={350}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              valueFormatter={(value) => formatCurrency(value)}
              tooltipTitle="Collection Stage Amount"
              tooltipContent={
                "Distribution of collection stages weighted by total dollar amount.\n\nUse this to identify where the largest financial exposure sits, even if invoice/service volume is lower.\n\nPair with service-count view for balanced prioritization."
              }
            />
          </Grid>
        </Grid>
      );
    },
    [
      chartColors.warning,
      chartColors.error,
      chartColors.info,
      handleAgingSliceClick,
      handleClientBarClick,
      handlePrivatePersonsUnpaidClick,
    ],
  );

  return (
    <>
      <ReportScopeToggle
        openOnly={openOnly}
        onChange={setOpenOnly}
        openLabel="All open invoices (as of today)"
        periodLabel="Invoices for the selected period"
      />
      <DashboardReport
        title="Outstanding Invoices"
        description="Advanced accounts receivable report using invoice-level paid/unpaid status, plus dedicated visibility into unpaid Private Persons services." 
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Invoice Timeline Report - lifecycle analysis for invoice creation/due/payment patterns
 */
export const InvoiceTimeline = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/invoice/timeline", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  const openTimelineDrawer = useCallback((title, subtitle, description, rows) => {
    const sortedRows = [...rows].sort((a, b) => {
      const aDate = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
      const bDate = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
      return bDate - aDate;
    });

    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: sortedRows,
      columns: [
        { field: "invoiceNumber", headerName: "Invoice #", width: 120 },
        { field: "clientName", headerName: "Client", width: 210 },
        {
          field: "invoiceDate",
          headerName: "Created",
          width: 125,
          renderCell: (row) => formatDate(row.invoiceDate),
        },
        {
          field: "dueDate",
          headerName: "Due",
          width: 125,
          renderCell: (row) => formatDate(row.dueDate),
        },
        {
          field: "paymentDate",
          headerName: "Paid",
          width: 125,
          renderCell: (row) => formatDate(row.paymentDate),
        },
        {
          field: "amount",
          headerName: "Amount",
          width: 130,
          renderCell: (row) => formatCurrency(row.amount),
        },
        {
          field: "daysInvoiceToDue",
          headerName: "Create→Due",
          width: 110,
          renderCell: (row) =>
            row.daysInvoiceToDue == null ? "-" : `${row.daysInvoiceToDue}d`,
        },
        {
          field: "daysInvoiceToPayment",
          headerName: "Create→Paid",
          width: 110,
          renderCell: (row) =>
            row.daysInvoiceToPayment == null
              ? "-"
              : `${row.daysInvoiceToPayment}d`,
        },
        {
          field: "daysLateAtPayment",
          headerName: "Late Days",
          width: 95,
          renderCell: (row) =>
            row.daysLateAtPayment == null ? "-" : `${row.daysLateAtPayment}d`,
        },
        { field: "paymentTiming", headerName: "Timing", width: 170 },
        {
          field: "status",
          headerName: "Status",
          width: 130,
          renderCell: (row) => (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color:
                  row.status === "Paid"
                    ? "success.main"
                    : row.status === "Overdue Unpaid"
                      ? "error.main"
                      : "warning.main",
              }}
            >
              {row.status}
            </Typography>
          ),
        },
      ],
      summary: {
        metrics: [
          {
            label: "Invoices",
            value: sortedRows.length,
          },
          {
            label: "Total Amount",
            value: formatCurrency(
              sortedRows.reduce((sum, row) => sum + (row.amount || 0), 0),
            ),
          },
          {
            label: "Paid",
            value: sortedRows.filter((row) => row.isPaid).length,
          },
        ],
      },
    });
  }, []);

  const handleAllInvoicesClick = useCallback(
    (data) => {
      const rows = data.timelineRows || [];
      openTimelineDrawer(
        "Invoice Timeline - All Invoices",
        `${rows.length} invoices • ${formatCurrency(data.summary?.totalAmount || 0)}`,
        "Lifecycle details across creation date, due date, and payment date to highlight timeline and delay patterns.",
        rows,
      );
    },
    [openTimelineDrawer],
  );

  const handleOverdueUnpaidClick = useCallback(
    (data) => {
      const rows = (data.timelineRows || []).filter((row) => row.isOverdueUnpaid);
      openTimelineDrawer(
        "Overdue Unpaid Invoices",
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, row) => sum + (row.amount || 0), 0),
        )}`,
        "Unpaid invoices with due dates in the past, requiring immediate follow-up.",
        rows,
      );
    },
    [openTimelineDrawer],
  );

  const handlePaidInvoicesClick = useCallback(
    (data) => {
      const rows = (data.timelineRows || []).filter((row) => row.isPaid);
      openTimelineDrawer(
        "Paid Invoices",
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, row) => sum + (row.amount || 0), 0),
        )}`,
        "Invoices that have a recorded payment date.",
        rows,
      );
    },
    [openTimelineDrawer],
  );

  const handleUnpaidInvoicesClick = useCallback(
    (data) => {
      const rows = (data.timelineRows || []).filter((row) => !row.isPaid);
      openTimelineDrawer(
        "Unpaid Invoices",
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, row) => sum + (row.amount || 0), 0),
        )}`,
        "Invoices with no payment recorded yet.",
        rows,
      );
    },
    [openTimelineDrawer],
  );

  const handleDelayBucketClick = useCallback(
    (slice, data) => {
      const bucketName = String(slice.label || "").replace(/\s*\([^)]*\)\s*$/, "");
      const rows = (data.timelineRows || []).filter(
        (row) => row.paymentTiming === bucketName,
      );
      openTimelineDrawer(
        `Payment Timing - ${bucketName}`,
        `${rows.length} invoices • ${formatCurrency(
          rows.reduce((sum, row) => sum + (row.amount || 0), 0),
        )}`,
        "Invoices in the selected payment timing bucket.",
        rows,
      );
    },
    [openTimelineDrawer],
  );

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      return [
        {
          groupTitle: "Timeline Health",
          metrics: [
            {
              title: "Total Invoices",
              value: `${summary.totalInvoices || 0}`,
              subtitle: formatCurrency(summary.totalAmount || 0),
              tooltipTitle: "Total Invoices",
              tooltipContent:
                "Definition: Count of all invoices in the selected filter window.\n\nAmount shown below the count is the sum of invoice amounts for this same set.\n\nUse this as your baseline population before evaluating paid/unpaid behavior and delays.",
              onClick: () => handleAllInvoicesClick(data),
            },
            {
              title: "Paid Invoices",
              value: `${summary.paidInvoices || 0}`,
              subtitle: `${(summary.paidRate || 0).toFixed(2)}% paid rate`,
              tooltipTitle: "Paid Invoices",
              tooltipContent:
                "Definition: Invoices with a populated payment date.\n\nPaid rate = (Paid Invoices / Total Invoices) × 100.\n\nClick this card to open the exact paid invoice list, including create→due and create→paid timing fields.",
              onClick: () => handlePaidInvoicesClick(data),
            },
            {
              title: "Unpaid Invoices",
              value: `${summary.unpaidInvoices || 0}`,
              subtitle: formatCurrency(summary.unpaidAmount || 0),
              color: "warning.main",
              tooltipTitle: "Unpaid Invoices",
              tooltipContent:
                "Definition: Invoices without a recorded payment date.\n\nAmount shown is the full unpaid invoice amount still open in AR.\n\nClick this card to review the unpaid ledger and prioritize follow-up.",
              onClick: () => handleUnpaidInvoicesClick(data),
            },
            {
              title: "Overdue Unpaid",
              value: `${summary.overdueUnpaidInvoices || 0}`,
              subtitle: formatCurrency(summary.overdueUnpaidAmount || 0),
              color: "error.main",
              tooltipTitle: "Overdue Unpaid",
              tooltipContent:
                "Definition: Unpaid invoices where due date is before today.\n\nAmount shown is overdue AR exposure that is already late.\n\nClick to open only overdue unpaid invoices for immediate collections escalation.",
              onClick: () => handleOverdueUnpaidClick(data),
            },
          ],
        },
        {
          groupTitle: "Payment Delays",
          metrics: [
            {
              title: "On-Time Payment Rate",
              value: `${(summary.onTimeRate || 0).toFixed(2)}%`,
              subtitle: "Among paid invoices",
              tooltipTitle: "On-Time Payment Rate",
              tooltipContent:
                "Definition: Share of paid invoices with payment date on or before due date.\n\nCalculated only from paid invoices to avoid distortion from open items.\n\nLower values indicate recurring late payment behavior across clients.",
            },
            {
              title: "Avg Create→Due",
              value: `${(summary.avgInvoiceToDueDays || 0).toFixed(1)} days`,
              subtitle: "Invoice terms cadence",
              tooltipTitle: "Average Create→Due",
              tooltipContent:
                "Definition: Average number of days between invoice date and due date.\n\nRepresents your effective payment terms cadence in practice.\n\nUse with Create→Paid to see whether delays are caused by terms or by collection lag.",
            },
            {
              title: "Avg Create→Paid",
              value: `${(summary.avgInvoiceToPaymentDays || 0).toFixed(1)} days`,
              subtitle: "Collection cycle time",
              tooltipTitle: "Average Create→Paid",
              tooltipContent:
                "Definition: Average number of days from invoice creation to payment receipt (paid invoices only).\n\nThis is your realized cash-conversion cycle at invoice level.\n\nTrack this trend to measure collection efficiency over time.",
            },
            {
              title: "Avg Late Days",
              value: `${(summary.avgLatePaidDays || 0).toFixed(1)} days`,
              subtitle: "Late-paid invoices only",
              color: "warning.main",
              tooltipTitle: "Average Late Days",
              tooltipContent:
                "Definition: Average of payment-date minus due-date, for invoices that were paid late.\n\nA value above 0 means payments are arriving after due date.\n\nHigher values suggest slower post-due collections and rising AR risk.",
            },
          ],
        },
      ];
    },
    [
      handleAllInvoicesClick,
      handleOverdueUnpaidClick,
      handlePaidInvoicesClick,
      handleUnpaidInvoicesClick,
    ],
  );

  const chartsConfig = useCallback(
    (data) => {
      const monthly = data.monthlyTimeline || {};
      const monthRows = (monthly.months || []).map((month, idx) => ({
        month,
        issuedCount: monthly.issuedCount?.[idx] || 0,
        dueCount: monthly.dueCount?.[idx] || 0,
        paidCount: monthly.paidCount?.[idx] || 0,
        issuedAmount: monthly.issuedAmount?.[idx] || 0,
        paidAmount: monthly.paidAmount?.[idx] || 0,
      }));

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Monthly Invoice Lifecycle (Count)"
              data={monthRows}
              xAxisKey="month"
              series={[
                {
                  dataKey: "issuedCount",
                  label: "Created",
                  color: chartColors.info,
                },
                {
                  dataKey: "dueCount",
                  label: "Due",
                  color: chartColors.warning,
                },
                {
                  dataKey: "paidCount",
                  label: "Paid",
                  color: chartColors.success,
                },
              ]}
              height={350}
              yAxisLabel="Invoices"
              yAxisScaleType="linear"
              valueFormatter={(value) => `${(value || 0).toLocaleString()} invoices`}
              tooltipTitle="Invoice Timeline"
              tooltipContent={
                "What this shows:\n- Created: invoices issued each month\n- Due: invoices reaching due date each month\n- Paid: invoices paid each month\n\nHow to read it:\n- Created > Paid over multiple months indicates collection backlog growth.\n- High Due with low Paid signals near-term cash pressure.\n- Persistent lag between Due and Paid indicates payment delay behavior."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Monthly Invoice Amount vs Collected"
              data={monthRows}
              xAxisKey="month"
              series={[
                {
                  dataKey: "issuedAmount",
                  label: "Invoiced Amount",
                  color: chartColors.warning,
                },
                {
                  dataKey: "paidAmount",
                  label: "Collected Amount",
                  color: chartColors.success,
                },
              ]}
              height={350}
              yAxisLabel="Amount ($)"
              yAxisScaleType="linear"
              valueFormatter={(value) => formatCurrency(value)}
              tooltipTitle="Cash Timeline"
              tooltipContent={
                "What this shows:\n- Invoiced Amount: dollar value billed each month\n- Collected Amount: dollar value paid each month\n\nHow to read it:\n- Invoiced > Collected means AR is accumulating.\n- Closing the gap month-over-month indicates improved collections.\n- Large spikes can reveal concentration risk tied to a few invoices."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Payment Timing Buckets"
              data={(data.delayBuckets || []).map((bucket) => ({
                id: bucket.id,
                label: `${bucket.timing} (${bucket.invoiceCount})`,
                value: bucket.invoiceCount || 0,
              }))}
              height={350}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              valueFormatter={(value) => `${(value || 0).toLocaleString()} invoices`}
              onSliceClick={(item) => handleDelayBucketClick(item, data)}
              tooltipTitle="Payment Timing"
              tooltipContent={
                "Distribution of invoices by timing bucket (early, on-time, 1-15 late, 16-30 late, 31+ late, overdue unpaid).\n\nUse this to quickly identify where invoice volume is concentrated in the delay lifecycle.\n\nClick any slice to open the exact invoice list behind that bucket."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Payment Timing by Amount"
              data={(data.delayBuckets || [])
                .filter((bucket) => (bucket.totalAmount || 0) > 0)
                .map((bucket) => ({
                  id: bucket.id,
                  label: `${bucket.timing} (${formatCurrency(bucket.totalAmount || 0)})`,
                  value: bucket.totalAmount || 0,
                }))}
              height={350}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              valueFormatter={(value) => formatCurrency(value)}
              tooltipTitle="Payment Timing Amount"
              tooltipContent={
                "Same timing buckets as the count chart, but weighted by total invoice amount.\n\nUse this to find where the largest dollar exposure sits, even if invoice count is small.\n\nThis helps prioritize collection work by financial impact, not just volume."
              }
            />
          </Grid>
        </Grid>
      );
    },
    [
      chartColors.info,
      chartColors.warning,
      chartColors.success,
      handleDelayBucketClick,
    ],
  );

  return (
    <>
      <DashboardReport
        title="Invoice Timeline"
        description="Visual timeline of invoice creation, due dates, and payment dates to identify payment patterns, delay buckets, and collection cycle risk."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Aging & Collections Report - AR aging buckets and collection pressure
 */
export const InvoiceAgingCollections = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [openOnly, setOpenOnly] = useState(true);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/invoice/outstanding", {
      params: { ...activeFilters, openOnly: openOnly ? 1 : 0 },
    });
    return response.data;
  }, [openOnly]);

  const openDrawer = useCallback((title, subtitle, description, rows) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: [...rows].sort(
        (a, b) => (b.outstandingAmount || 0) - (a.outstandingAmount || 0),
      ),
      columns: [
        { field: "invoiceNumber", headerName: "Invoice #", width: 120 },
        { field: "clientName", headerName: "Client", width: 240 },
        {
          field: "dueDate",
          headerName: "Due Date",
          width: 130,
          renderCell: (row) => formatDate(row.dueDate),
        },
        {
          field: "daysPastDue",
          headerName: "Days Past Due",
          width: 120,
          renderCell: (row) =>
            row.daysPastDue == null ? "-" : `${row.daysPastDue} days`,
        },
        {
          field: "agingBucket",
          headerName: "Aging Bucket",
          width: 130,
        },
        {
          field: "outstandingAmount",
          headerName: "Outstanding",
          width: 140,
          renderCell: (row) => formatCurrency(row.outstandingAmount),
        },
        {
          field: "collectionProgress",
          headerName: "Collected %",
          width: 120,
          renderCell: (row) => `${(row.collectionProgress || 0).toFixed(1)}%`,
        },
        { field: "riskLevel", headerName: "Risk", width: 100 },
      ],
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const rows = data.invoices || [];

      return [
        {
          groupTitle: "Aging Exposure",
          metrics: [
            {
              title: "Total Outstanding",
              value: formatCurrency(summary.totalOutstandingAmount || 0),
              subtitle: `${summary.totalOutstandingInvoices || 0} invoices`,
              tooltipTitle: "Total Outstanding",
              tooltipContent: `Total unpaid invoice exposure in the selected scope.

Current period context:
- Outstanding Amount: ${formatCurrency(summary.totalOutstandingAmount || 0)}
- Outstanding Invoices: ${summary.totalOutstandingInvoices || 0}

Use this as the top-line AR burden indicator before aging segmentation.`,
              color: "warning.main",
              onClick: () =>
                openDrawer(
                  "All Outstanding Invoices",
                  `${rows.length} invoices`,
                  "All unpaid invoice-level AR exposure.",
                  rows,
                ),
            },
            {
              title: "Overdue",
              value: formatCurrency(summary.overdueAmount || 0),
              subtitle: `${summary.overdueInvoices || 0} invoices`,
              tooltipTitle: "Overdue",
              tooltipContent: `Outstanding balance already past due date.

Current period context:
- Overdue Amount: ${formatCurrency(summary.overdueAmount || 0)}
- Overdue Invoices: ${summary.overdueInvoices || 0}

This is your immediate collection pressure bucket and should be reviewed with aging distribution.`,
              color: "error.main",
              onClick: () =>
                openDrawer(
                  "Overdue Invoices",
                  `${summary.overdueInvoices || 0} overdue invoices`,
                  "Invoices past due date and unpaid.",
                  rows.filter(
                    (row) => row.daysPastDue != null && row.daysPastDue > 0,
                  ),
                ),
            },
            {
              title: "Critical 90+",
              value: formatCurrency(summary.criticalAmount || 0),
              subtitle: `${summary.criticalInvoices || 0} invoices`,
              tooltipTitle: "Critical 90+",
              tooltipContent: `Aging segment with invoices more than 90 days overdue.

Current period context:
- Critical Amount: ${formatCurrency(summary.criticalAmount || 0)}
- Critical Invoices: ${summary.criticalInvoices || 0}

These receivables carry the highest non-recovery risk and should be escalated first.`,
              color: "error.main",
              onClick: () =>
                openDrawer(
                  "Critical Aging 90+",
                  `${summary.criticalInvoices || 0} invoices`,
                  "Invoices with highest aging urgency (more than 90 days past due).",
                  rows.filter(
                    (row) => row.daysPastDue != null && row.daysPastDue > 90,
                  ),
                ),
            },
            {
              title: "Average Past Due",
              value: `${(summary.avgDaysPastDue || 0).toFixed(1)} days`,
              subtitle: `${(summary.collectionRate || 0).toFixed(2)}% collection rate`,
              tooltipTitle: "Average Past Due",
              tooltipContent: `Average number of days invoices are currently past due, paired with collection conversion context.

Current period context:
- Avg Past Due: ${(summary.avgDaysPastDue || 0).toFixed(1)} days
- Collection Rate: ${(summary.collectionRate || 0).toFixed(2)}%

Interpretation:
- Rising past-due days with declining collection rate indicates AR process stress.
- Lower days with stable collection rate indicates healthier receivable turn.`,
              onClick: () =>
                openDrawer(
                  "Collections - Full Ledger",
                  `${rows.length} invoices in scope`,
                  "Use this ledger to prioritize and sequence AR follow-up.",
                  rows,
                ),
            },
          ],
        },
      ];
    },
    [openDrawer],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Aging Distribution by Amount"
            tooltipTitle="Aging Distribution by Amount"
            tooltipContent="Shows how outstanding dollars are distributed across aging buckets. Use this to identify where monetary risk is concentrated and whether AR is migrating toward older, harder-to-collect bands."
            data={(data.agingData || []).map((bucket) => ({
              id: bucket.id,
              label: `${bucket.label} (${bucket.invoiceCount})`,
              value: bucket.value || 0,
            }))}
            height={360}
            valueFormatter={formatCurrency}
            onSliceClick={(slice) =>
              openDrawer(
                `Aging Bucket: ${slice.label}`,
                "Invoices in selected bucket",
                "Aging bucket drilldown with invoice-level amounts.",
                (data.invoices || []).filter((row) =>
                  String(slice.label).startsWith(row.agingBucket || ""),
                ),
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Top Clients by Outstanding AR"
            tooltipTitle="Top Clients by Outstanding AR"
            tooltipContent="Ranks clients by unpaid AR amount so collections work can be prioritized by financial impact. Click a client bar to open invoice-level exposure and target outreach sequencing."
            data={(data.topClients || []).map((row) => ({
              clientName: row.clientName,
              outstandingAmount: row.outstandingAmount || 0,
              invoiceCount: row.invoiceCount || 0,
            }))}
            xAxisKey="clientName"
            series={[
              {
                dataKey: "outstandingAmount",
                label: "Outstanding",
                color: chartColors.warning,
              },
            ]}
            height={360}
            showLegend={false}
            yAxisLabel="Outstanding ($)"
            yAxisScaleType="linear"
            valueFormatter={formatCurrency}
            onBarClick={(bar) =>
              openDrawer(
                `Client AR: ${bar.clientName}`,
                "Client-level outstanding invoices",
                "Outstanding exposure for the selected client.",
                (data.invoices || []).filter(
                  (row) => row.clientName === bar.clientName,
                ),
              )
            }
          />
        </Grid>
      </Grid>
    ),
    [chartColors.warning, openDrawer],
  );

  return (
    <>
      <ReportScopeToggle
        openOnly={openOnly}
        onChange={setOpenOnly}
        openLabel="All open invoices (as of today)"
        periodLabel="Invoices for the selected period"
      />
      <DashboardReport
        title="Aging & Collections"
        description="A/R aging concentration, overdue pressure, and critical invoice exposure for collection prioritization."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Client Payment Behavior & Risk Report - payment speed and reliability by client
 */
export const ClientPaymentBehaviorRisk = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [paymentRangesResponse, outstandingResponse, timelineResponse] =
      await Promise.all([
        api.get("/reports/financial/payment-ranges", { params: activeFilters }),
        api.get("/reports/invoice/outstanding", { params: activeFilters }),
        api.get("/reports/invoice/timeline", { params: activeFilters }),
      ]);

    const ranges = paymentRangesResponse.data?.data?.ranges || [];
    const invoices = outstandingResponse.data?.data?.invoices || [];
    const timelineRows = timelineResponse.data?.data?.timelineRows || [];

    const clientMap = {};

    ranges.forEach((range) => {
      (range.clients || []).forEach((client) => {
        if (!clientMap[client.clientId]) {
          clientMap[client.clientId] = {
            clientId: client.clientId,
            clientName: client.clientName,
            avgPaymentDays: client.avgPaymentDays || 0,
            invoiceCount: client.invoiceCount || 0,
            invoiceAmount: client.totalRevenue || 0,
            overdueAmount: 0,
            overdueInvoices: 0,
            paidInvoices: 0,
            latePaidInvoices: 0,
            paymentRange: range.label,
          };
        }
      });
    });

    invoices.forEach((invoice) => {
      const key = Object.values(clientMap).find(
        (row) => row.clientName === invoice.clientName,
      )?.clientId;
      if (!key || !clientMap[key]) return;
      if (invoice.daysPastDue != null && invoice.daysPastDue > 0) {
        clientMap[key].overdueAmount += invoice.outstandingAmount || 0;
        clientMap[key].overdueInvoices += 1;
      }
    });

    timelineRows.forEach((row) => {
      const key = Object.values(clientMap).find(
        (item) => item.clientName === row.clientName,
      )?.clientId;
      if (!key || !clientMap[key]) return;
      if (row.isPaid) {
        clientMap[key].paidInvoices += 1;
      }
      if (row.daysLateAtPayment != null && row.daysLateAtPayment > 0) {
        clientMap[key].latePaidInvoices += 1;
      }
    });

    const clientRows = Object.values(clientMap)
      .map((row) => {
        const paidRate =
          row.invoiceCount > 0 ? (row.paidInvoices / row.invoiceCount) * 100 : 0;
        const lateRate =
          row.paidInvoices > 0
            ? (row.latePaidInvoices / row.paidInvoices) * 100
            : 0;

        const riskScore =
          row.avgPaymentDays * 0.5 +
          lateRate * 0.2 +
          (row.overdueInvoices > 0 ? 20 : 0) +
          (row.overdueAmount > 0 ? Math.min(row.overdueAmount / 1000, 30) : 0);

        const riskLevel =
          riskScore >= 70 ? "High" : riskScore >= 40 ? "Medium" : "Low";

        return {
          ...row,
          paidRate: parseFloat(paidRate.toFixed(2)),
          lateRate: parseFloat(lateRate.toFixed(2)),
          riskScore: parseFloat(riskScore.toFixed(1)),
          riskLevel,
        };
      })
      .sort((a, b) => b.riskScore - a.riskScore);

    return {
      data: {
        clientRows,
        riskDistribution: ["High", "Medium", "Low"].map((label, index) => ({
          id: index + 1,
          label,
          value: clientRows.filter((row) => row.riskLevel === label).length,
        })),
      },
    };
  }, []);

  const openClientDrawer = useCallback((data, clientName = null) => {
    const rows = clientName
      ? (data.clientRows || []).filter((row) => row.clientName === clientName)
      : data.clientRows || [];

    setDrillDown({
      open: true,
      title: clientName
        ? `Client Payment Risk: ${clientName}`
        : "Client Payment Behavior",
      subtitle: `${rows.length} client${rows.length === 1 ? "" : "s"}`,
      description:
        "Risk model combines payment speed, late payment rate, and overdue exposure.",
      data: rows,
      columns: [
        { field: "clientName", headerName: "Client", width: 250 },
        { field: "paymentRange", headerName: "Range", width: 120 },
        {
          field: "avgPaymentDays",
          headerName: "Avg Days",
          width: 100,
          renderCell: (row) => `${row.avgPaymentDays || 0} days`,
        },
        { field: "invoiceCount", headerName: "Invoices", width: 90 },
        {
          field: "invoiceAmount",
          headerName: "Invoice Amount",
          width: 140,
          renderCell: (row) => formatCurrency(row.invoiceAmount),
        },
        {
          field: "overdueAmount",
          headerName: "Overdue",
          width: 120,
          renderCell: (row) => formatCurrency(row.overdueAmount),
        },
        {
          field: "paidRate",
          headerName: "Paid Rate",
          width: 100,
          renderCell: (row) => `${row.paidRate.toFixed(1)}%`,
        },
        {
          field: "lateRate",
          headerName: "Late Rate",
          width: 100,
          renderCell: (row) => `${row.lateRate.toFixed(1)}%`,
        },
        {
          field: "riskScore",
          headerName: "Risk Score",
          width: 100,
        },
        { field: "riskLevel", headerName: "Risk", width: 90 },
      ],
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const rows = data.clientRows || [];
      const highRisk = rows.filter((row) => row.riskLevel === "High");
      const mediumRisk = rows.filter((row) => row.riskLevel === "Medium");

      return [
        {
          groupTitle: "Risk Overview",
          metrics: [
            {
              title: "Tracked Clients",
              value: rows.length,
              subtitle: "With measurable invoice behavior",
              tooltipTitle: "Tracked Clients",
              tooltipContent: `Number of clients with enough invoice/timeline history to calculate payment behavior and risk signals.

Current period context:
- Tracked Clients: ${rows.length}

Broader tracked coverage improves reliability of risk segmentation and trend monitoring.`,
              onClick: () => openClientDrawer(data),
            },
            {
              title: "High Risk Clients",
              value: highRisk.length,
              subtitle: formatCurrency(
                highRisk.reduce((sum, row) => sum + (row.overdueAmount || 0), 0),
              ),
              tooltipTitle: "High Risk Clients",
              tooltipContent: `Clients with the highest payment risk profile based on late behavior, overdue exposure, and composite scoring.

Current period context:
- High-Risk Clients: ${highRisk.length}
- High-Risk Overdue Exposure: ${formatCurrency(
                highRisk.reduce((sum, row) => sum + (row.overdueAmount || 0), 0),
              )}

Use as an intervention queue for collections and account escalation.`,
              color: "error.main",
              onClick: () =>
                openClientDrawer({
                  ...data,
                  clientRows: highRisk,
                }),
            },
            {
              title: "Medium Risk Clients",
              value: mediumRisk.length,
              subtitle: formatCurrency(
                mediumRisk.reduce((sum, row) => sum + (row.overdueAmount || 0), 0),
              ),
              tooltipTitle: "Medium Risk Clients",
              tooltipContent: `Clients with moderate deterioration signals that may convert to high risk without intervention.

Current period context:
- Medium-Risk Clients: ${mediumRisk.length}
- Medium-Risk Overdue Exposure: ${formatCurrency(
                mediumRisk.reduce((sum, row) => sum + (row.overdueAmount || 0), 0),
              )}

Focus on preventive action here to reduce future high-risk migration.`,
              color: "warning.main",
              onClick: () =>
                openClientDrawer({
                  ...data,
                  clientRows: mediumRisk,
                }),
            },
            {
              title: "Average Risk Score",
              value:
                rows.length > 0
                  ? (
                      rows.reduce((sum, row) => sum + (row.riskScore || 0), 0) /
                      rows.length
                    ).toFixed(1)
                  : "0.0",
              subtitle: "Composite payment reliability score",
              tooltipTitle: "Average Risk Score",
              tooltipContent: `Portfolio-level average of client risk scores.

Score components include:
- Average payment delay
- Late-paid ratio
- Presence of overdue invoices
- Size of overdue exposure

Use this value to track whether payment quality is improving or deteriorating over time.`,
              onClick: () => openClientDrawer(data),
            },
          ],
        },
      ];
    },
    [openClientDrawer],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Client Risk Distribution"
            tooltipTitle="Client Risk Distribution"
            tooltipContent="Distribution of clients across High, Medium, and Low payment-risk tiers. Monitor the High tier share for immediate collection risk and the Medium tier as the leading indicator of pipeline deterioration."
            data={data.riskDistribution || []}
            height={360}
            valueFormatter={(value) => `${value || 0} clients`}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Top Clients by Risk Score"
            tooltipTitle="Top Clients by Risk Score"
            tooltipContent="Ranks clients by composite payment-risk score. Use this to prioritize account outreach, tighten terms, and coordinate proactive collection workflows for the highest-risk accounts first."
            data={(data.clientRows || []).slice(0, 12).map((row) => ({
              clientName: row.clientName,
              riskScore: row.riskScore || 0,
            }))}
            xAxisKey="clientName"
            series={[
              {
                dataKey: "riskScore",
                label: "Risk Score",
                color: chartColors.error,
              },
            ]}
            height={360}
            showLegend={false}
            yAxisLabel="Risk Score"
            yAxisScaleType="linear"
            valueFormatter={(value) => `${(value || 0).toFixed(1)} score`}
            onBarClick={(bar) => openClientDrawer(data, bar.clientName)}
          />
        </Grid>
      </Grid>
    ),
    [chartColors.error, openClientDrawer],
  );

  return (
    <>
      <DashboardReport
        title="Client Payment Behavior & Risk"
        description="Client-level payment speed, late-payment behavior, and overdue exposure combined into a risk score."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const INVOICE_REPORTS = {
  "outstanding-invoices": OutstandingInvoices,
  "invoice-aging": InvoiceAgingCollections,
  "invoice-timeline": InvoiceTimeline,
  "payment-behavior": ClientPaymentBehaviorRisk,
  "discount-leakage": DiscountLeakage,
  "cashflow-forecast": CashFlowForecast,
};
